import React, { useContext, useEffect, useState, useRef } from "react";
import {
	Card,
	CardContent,
	IconButton,
	Tooltip,
	Typography,
	styled,
	Box,
	Grid,
	CardHeader,
	CardActions,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Tesseract from "tesseract.js";
import AlertMessage from "../../../Alert";
import docBackUploader from "../../../../assets/img/mockups/doc-back-uploader.png";
import docFrontUploader from "../../../../assets/img/mockups/doc-front-uploader.png";
import docFrontUploaderSuccess from "../../../../assets/img/mockups/doc-front-uploader-success.png";
import docBackUploaderSuccess from "../../../../assets/img/mockups/doc-back-uploader-success.png";
import docBackUploaderError from "../../../../assets/img/mockups/doc-back-uploader-error.png";
import docFrontUploaderError from "../../../../assets/img/mockups/doc-front-uploader-error.png";
import checkMP from "../../../../assets/img/mockups/onboarding_check.png";
import "./style.css";
import { InterrogacaoMP } from "../../../Icons";
import { CadastroContext } from "../../../../contexts/CadastroContext";
import api from "../../../../services/api";
import {session, StorageKeys} from "../../../../services/storage.service";
import {AsoInformation} from "../../../../types/aso-information";

const InputHidden = styled("input")({
	display: "none",
});

const UploaderContainer = styled(Card)(({ theme }: any) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	padding: theme.spacing(2),
	textAlign: "center",
	height: 140,
	boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const AsoValidarIdentidade = () => {
	const navigate = useNavigate();
	const asoInformation = session.get<AsoInformation>(
		StorageKeys.ASO_INFORMATION,
	)!;
	const { cadastro, handleCadastroChange } = useContext(CadastroContext);
	const [alert, setAlert] = useState({
		show: false,
		message: "",
		variant: "success",
	});
	const [frontError, setFrontError] = useState(false);
	const [backError, setBackError] = useState(false);
	const [loading, setLoading]: any = useState({ front: false, back: false });
	const [arquivoValidoLadoComFoto, setArquivoValidoLadoComFoto] =
		useState(false);
	const [arquivoValidoLadoSemFoto, setArquivoValidoLadoSemFoto] =
		useState(false);
	const { dbName } = useParams();
	const location = useLocation();

	// Estado para controle do menu de opções (câmera/galeria)
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [currentSide, setCurrentSide] = useState<string>("");
	const cameraInputFrontRef = useRef<HTMLInputElement>(null);
	const cameraInputBackRef = useRef<HTMLInputElement>(null);
	const galleryInputFrontRef = useRef<HTMLInputElement>(null);
	const galleryInputBackRef = useRef<HTMLInputElement>(null);

	// Detectar se é dispositivo móvel
	const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
		navigator.userAgent
	);

	useEffect(() => {
		if (arquivoValidoLadoComFoto && arquivoValidoLadoSemFoto) {
			setTimeout(() => {
				if(!asoInformation.primeiraVez)
				{
					return navigate(`/aso/registro/aviso-reconhecimento-facial/${asoInformation.dbName}/${cadastro.userId}`);
				}
				if (cadastro.isEmpresa && !cadastro.reaproveitamentoAso) {
					navigate(`/aso/registro/validar-info-empresa/${cadastro.idConsulta}`);
				} else if (cadastro.reaproveitamentoAso) {
					navigate("/aso/registro/validar-info-empresa");
				} else {
					if (
						location.pathname ===
						`/aso/registro/validar-identidade-aso/${dbName}`
					) {
						return navigate(`/aso/registro/validar-info-aso/${dbName}`);
					} else {
						navigate(
							`/aso/registro/validar-info/${cadastro.idConsulta}/${dbName}`,
						);
					}
				}
			}, 2000);
		}
	}, [
		arquivoValidoLadoComFoto,
		arquivoValidoLadoSemFoto,
		navigate,
		cadastro.idConsulta,
		cadastro.isEmpresa,
		cadastro.reaproveitamentoAso,
		dbName,
		location.pathname,
	]);

	useEffect(() => {
		console.log(
			"Estado de validação alterado:",
			arquivoValidoLadoComFoto,
			arquivoValidoLadoSemFoto,
		);
	}, [arquivoValidoLadoComFoto, arquivoValidoLadoSemFoto]);

	// Funções para controle do menu de opções
	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, side: string) => {
		if (side === "back" && !arquivoValidoLadoComFoto) return;

		if (isMobile) {
			setMenuAnchorEl(event.currentTarget);
			setCurrentSide(side);
		} else {
			// Em desktop, abre direto o seletor de arquivo
			const galleryRef = side === "front" ? galleryInputFrontRef : galleryInputBackRef;
			galleryRef.current?.click();
		}
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
		setCurrentSide("");
	};

	const handleCameraClick = () => {
		const cameraRef = currentSide === "front" ? cameraInputFrontRef : cameraInputBackRef;
		cameraRef.current?.click();
		handleCloseMenu();
	};

	const handleGalleryClick = () => {
		const galleryRef = currentSide === "front" ? galleryInputFrontRef : galleryInputBackRef;
		galleryRef.current?.click();
		handleCloseMenu();
	};

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
		side: string,
	) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setLoading((prev: any) => ({ ...prev, [side]: true }));
		const validTypes = ["image/png", "image/jpeg", "image/jpg"];
		if (!validTypes.includes(file.type)) {
			setAlert({
				show: true,
				message: "Formato de arquivo inválido.",
				variant: "danger",
			});
			side === "front" ? setFrontError(true) : setBackError(true);
			setLoading((prev: any) => ({ ...prev, [side]: false }));
			return;
		}

		side === "front" ? setFrontError(false) : setBackError(false);

		const reader = new FileReader();
		reader.onload = async () => {
			const base64: any = reader.result;
			try {
				await api.post("/salvar-documentos", {
					email: cadastro.email,
					[side === "front" ? "frenteDocumento" : "versoDocumento"]: base64,
					dbName: dbName,
				});
				if (side === "front") {
					setArquivoValidoLadoComFoto(true);
					handleCadastroChange({
						frenteDocumentoValidado: true,
						frenteDocumento: "ok",
					});
				} else {
					setArquivoValidoLadoSemFoto(true);
					handleCadastroChange({
						versoDocumentoValidado: true,
						versoDocumento: "ok",
					});
				}
			} catch (error) {
				console.error("Erro ao salvar documentos: ", error);
				setAlert({
					show: true,
					message: "Erro ao salvar documentos.",
					variant: "danger",
				});
				side === "front" ? setFrontError(true) : setBackError(true);
			}
			setLoading((prev: any) => ({ ...prev, [side]: false }));
		};
		reader.readAsDataURL(file);
	};

	const renderUploader = (side: string) => {
		const isFront = side === "front";
		let backgroundImage: string;
		let borderColor: string;
		let backgroundColor: string;

		if (loading[side]) {
			backgroundImage = "none";
			borderColor = "#009ee3";
			backgroundColor = "#f5f5f5";
		} else if ((isFront && frontError) || (!isFront && backError)) {
			backgroundImage = isFront ? docFrontUploaderError : docBackUploaderError;
			borderColor = "#e02020";
			backgroundColor = "#ffebeb";
		} else if (
			(isFront && arquivoValidoLadoComFoto) ||
			(!isFront && arquivoValidoLadoSemFoto)
		) {
			backgroundImage = isFront
				? docFrontUploaderSuccess
				: docBackUploaderSuccess;
			borderColor = "#00a650";
			backgroundColor = "#e6f4f1";
		} else {
			backgroundImage = isFront ? docFrontUploader : docBackUploader;
			borderColor = isFront || arquivoValidoLadoComFoto ? "#009ee3" : "#e0e0e0";
			backgroundColor = "#f5f5f5";
		}

		return (
			<UploaderContainer
				className="upload-area"
				sx={{
					display: "grid",
					justifyContent: "center",
					alignItems: "end",
					border: "2px solid",
					borderColor: borderColor,
					background: `${backgroundColor} url(${backgroundImage})`,
					backgroundSize: "45%",
					backgroundPosition: "50% 60%",
					backgroundRepeat: "no-repeat",
					cursor: side === "back" && !arquivoValidoLadoComFoto ? "not-allowed" : "pointer",
					opacity: side === "back" && !arquivoValidoLadoComFoto ? 0.6 : 1,
				}}
				onClick={(e) => handleOpenMenu(e, side)}
			>
				{loading[side] && (
					<div className="alert-loader">
						<div className="lds-ring">
							<div></div>
							<div></div>
							<div></div>
							<div></div>
						</div>
						<span className="text-loader">Falta pouco...</span>
					</div>
				)}

				<CardContent
					sx={{
						padding: 0,
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{/* Input para câmera (capture="environment" para câmera traseira) */}
					<InputHidden
						accept="image/*"
						capture="environment"
						type="file"
						ref={isFront ? cameraInputFrontRef : cameraInputBackRef}
						onChange={(e) => handleFileChange(e, side)}
						disabled={side === "back" && !arquivoValidoLadoComFoto}
					/>
					{/* Input para galeria (sem capture) */}
					<InputHidden
						accept="image/*"
						type="file"
						ref={isFront ? galleryInputFrontRef : galleryInputBackRef}
						onChange={(e) => handleFileChange(e, side)}
						disabled={side === "back" && !arquivoValidoLadoComFoto}
					/>
				</CardContent>
			</UploaderContainer>
		);
	};

	return (
		<Box sx={{ p: 3, maxWidth: "720px", margin: "0 auto", display: "grid" }}>
			<Typography
				variant="h6"
				sx={{ fontSize: "20px" }}
				className="title-aso-id"
			>
				Envie ou arraste uma foto da frente do seu documento. A foto do
				documento precisa estar próxima e bem iluminada.
			</Typography>

			<Card className="aso-card-hor">
				<CardHeader
					title={
						<Box
							display="flex"
							alignItems="center"
							justifyContent="space-between"
						>
							<Typography variant="body2" fontSize="1rem" component="span">
								Para esta validação, você pode usar sua CNH, RG, RNE ou CRNM.
							</Typography>
							<Tooltip
								title="Confira que o documento de identidade pertence a você e se todos os dados estão legíveis"
								style={{ backgroundColor: "#fff" }}
							>
								<IconButton
									sx={{ ml: 1, padding: "0", height: "24px", width: "24px" }}
								>
									<InterrogacaoMP />
								</IconButton>
							</Tooltip>
						</Box>
					}
					sx={{
						backgroundColor: "#ffffff",
						borderBottom: "1px solid #e0e0e0",
						px: 4,
					}}
				/>

				<CardContent sx={{ margin: "10px 20px" }}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							{renderUploader("front")}
						</Grid>
						<Grid item xs={12} sm={6}>
							{renderUploader("back")}
						</Grid>
					</Grid>
				</CardContent>

				<CardActions disableSpacing sx={{ p: 0 }}>
					<Typography
						variant="caption"
						display="block"
						sx={{
							mx: 2,
							p: "0 20px",
							fontSize: "10px",
							color: "rgba(0,0,0,.5)",
						}}
					>
						Cada arquivo deve ter no máximo 50MB e estar no formato .jpg, .jpeg,
						ou .png.
					</Typography>
				</CardActions>

				<AlertMessage
					show={alert.show}
					message={alert.message}
					variant={alert.variant}
					onClose={() => setAlert({ ...alert, show: false })}
				/>
			</Card>

			<Typography
				variant="caption"
				display="block"
				sx={{
					mx: 2,
					p: "0 20px",
					fontSize: "10px",
					margin: "0 auto",
					marginTop: "40px",
				}}
			>
				<img
					alt="Suas informações estão protegidas por rigorosos padrões de segurança."
					src={checkMP}
					style={{ width: "22px" }}
				/>
				Suas informações estão protegidas por rigorosos padrões de segurança.
			</Typography>

			{/* Menu de opções para dispositivos móveis */}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "center",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "center",
					horizontal: "center",
				}}
			>
				<MenuItem onClick={handleCameraClick}>
					<ListItemIcon>
						<CameraAltIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Tirar foto</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleGalleryClick}>
					<ListItemIcon>
						<PhotoLibraryIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Escolher da galeria</ListItemText>
				</MenuItem>
			</Menu>
		</Box>
	);
};

export default AsoValidarIdentidade;
